import { useMemo, useCallback } from "react"
import { compareThresholds } from "./utils"
import { ThresholdGroup } from "."

export function useThresholdValidation<T>(
   extremeThreshold: number,
   formState: T,
   thresholdGroups: ThresholdGroup<T>[],
   isDynamicToggleOn: boolean
) {
   const highThresholdInPercentRange = useMemo(() => {
      if (thresholdGroups.length >= 2) {
         const highKey = thresholdGroups[0].key
         const high = formState[highKey] as number
         return typeof high === "number" && high >= 0 && high <= 100
      }
      return false
   }, [formState, thresholdGroups])

   const lowThresholdInPercentRange = useMemo(() => {
      if (thresholdGroups.length >= 2) {
         const lowKey = thresholdGroups[1].key
         const low = formState[lowKey] as number
         return typeof low === "number" && low >= 0 && low <= 100
      }
      return false
   }, [formState, thresholdGroups])

   const highThresholdValid = useMemo(
      () =>
         thresholdGroups.length >= 2
            ? compareThresholds(
                 formState,
                 thresholdGroups[0],
                 thresholdGroups[1],
                 (a, b) => a > b
              )
            : true,
      [formState, thresholdGroups]
   )

   const lowThresholdValid = useMemo(
      () =>
         thresholdGroups.length >= 2
            ? compareThresholds(
                 formState,
                 thresholdGroups[1],
                 thresholdGroups[0],
                 (a, b) => a < b
              )
            : true,
      [formState, thresholdGroups]
   )

   const thresholdsEqual = useMemo(
      () =>
         thresholdGroups.length >= 2
            ? compareThresholds(
                 formState,
                 thresholdGroups[0],
                 thresholdGroups[1],
                 (a, b) => a === b
              )
            : false,
      [formState, thresholdGroups]
   )

   const highThresholdExceedExtreme = useMemo(() => {
      if (thresholdGroups.length >= 2) {
         const highKey = thresholdGroups[0].key
         const high = formState[highKey] as number
         return typeof high === "number" && high > extremeThreshold
      }
      return false
   }, [formState, thresholdGroups, extremeThreshold])

   const lowThresholdExceedExtreme = useMemo(() => {
      if (thresholdGroups.length >= 2) {
         const lowKey = thresholdGroups[1].key
         const low = formState[lowKey] as number
         return typeof low === "number" && low > extremeThreshold
      }
      return false
   }, [formState, thresholdGroups, extremeThreshold])

   const highThresholdNegative = useMemo(() => {
      if (thresholdGroups.length >= 2) {
         const highKey = thresholdGroups[0].key
         const high = formState[highKey] as number
         return typeof high === "number" && high < 0
      }
      return false
   }, [formState, thresholdGroups])

   const lowThresholdNegative = useMemo(() => {
      if (thresholdGroups.length >= 2) {
         const lowKey = thresholdGroups[1].key
         const low = formState[lowKey] as number
         return typeof low === "number" && low < 0
      }
      return false
   }, [formState, thresholdGroups])

   const getThresholdTooltip = useCallback(
      (key: string) => {
         if (isDynamicToggleOn) {
            if (!highThresholdInPercentRange && key === "high") {
               return { key, tooltipMessage: "Should be between 0-100" }
            }
            if (!lowThresholdInPercentRange && key === "low") {
               return { key, tooltipMessage: "Should be between 0-100" }
            }
         } else {
            if (highThresholdNegative && key === "high") {
               return { key, tooltipMessage: "Cannot be negative" }
            }
            if (lowThresholdNegative && key === "low") {
               return { key, tooltipMessage: "Cannot be negative" }
            }
            if (highThresholdExceedExtreme && key === "high") {
               return { key, tooltipMessage: "Cannot exceed extreme threshold" }
            }
            if (lowThresholdExceedExtreme && key === "low") {
               return { key, tooltipMessage: "Cannot exceed extreme threshold" }
            }
            if (thresholdsEqual && key === "low") {
               return { key, tooltipMessage: "Cannot be equal" }
            }
            if (!highThresholdValid && key === "high") {
               return {
                  key,
                  tooltipMessage: "Should be higher than low threshold",
               }
            }
            if (!lowThresholdValid && key === "low") {
               return {
                  key,
                  tooltipMessage: "Should be lower than high threshold",
               }
            }
         }
         return undefined
      },
      [
         isDynamicToggleOn,
         highThresholdInPercentRange,
         lowThresholdInPercentRange,
         highThresholdNegative,
         lowThresholdNegative,
         highThresholdExceedExtreme,
         highThresholdValid,
         lowThresholdExceedExtreme,
         lowThresholdValid,
         thresholdsEqual,
      ]
   )

   const thresholdsValid = useMemo(() => {
      return isDynamicToggleOn
         ? highThresholdInPercentRange && lowThresholdInPercentRange
         : highThresholdValid &&
              lowThresholdValid &&
              !highThresholdNegative &&
              !lowThresholdNegative &&
              !highThresholdExceedExtreme &&
              !lowThresholdExceedExtreme &&
              !thresholdsEqual
   }, [
      isDynamicToggleOn,
      highThresholdInPercentRange,
      lowThresholdInPercentRange,
      highThresholdValid,
      lowThresholdValid,
      highThresholdNegative,
      lowThresholdNegative,
      highThresholdExceedExtreme,
      lowThresholdExceedExtreme,
      thresholdsEqual,
   ])

   return {
      thresholdsValid,
      getThresholdTooltip,
   }
}
